// Copyright (c) 2020-2026 Life and Soft

import React, { useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useDispatch } from "react-redux";
import { LicenseInfo } from "@mui/x-license";

import "./App.scss";

import PrivateRoute from "./utils/privateRoute";
import Server from "./utils/axios/Server";
import routes from "./routes";
import { initConfiguration } from "./actions/configurationsActions";

const loading = () => (
  <div className="animated fadeIn pt-3 text-center">Loading...</div>
);

// Containers
const DefaultLayout = React.lazy(() => import("./containers/DefaultLayout"));

// Pages
const Error500 = React.lazy(() => import("./views/Errors/Error500"));
const Error404 = React.lazy(() => import("./views/Errors/Error404"));

const Login = React.lazy(() => import("./views/Login/Login"));
const Register = React.lazy(() => import("./views/Register/Register"));
const ForgotPassword = React.lazy(
  () => import("./views/Password/ForgotPassword"),
);
const ResetPassword = React.lazy(
  () => import("./views/Password/ResetPassword"),
);
const Confirmation = React.lazy(() => import("./views/Register/Confirmation"));
const LegalTerms = React.lazy(() => import("./views/LegalTerms/LegalTerms"));

LicenseInfo.setLicenseKey(
  "258842ae5fd5e6f255e4ae71e3e1d0a5Tz0xMzI2MTAsRT0xODE5OTI5NTk5MDAwLFM9cHJvLExNPWFubnVhbCxQVj1RMy0yMDI0LEtWPTI=",
);

const App = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [healthCheckOk, setHealthCheckOk] = useState(true);
  const [letRegister, setLetRegister] = useState("");

  useEffect(() => {
    const loadAppData = async () => {
      try {
        if (localStorage.jwtToken) {
          const configRes = await Server.get("/configuration");
          dispatch(initConfiguration(configRes.data));
        } else {
          try {
            const registerRes = await Server.get("/users/register");
            setLetRegister(registerRes.data);
          } catch {
            setLetRegister("");
          }
        }
      } catch (err) {
        console.log(err);
      } finally {
        setIsLoading(false);
      }
    };

    Server.get("/status")
      .then(() => {
        setHealthCheckOk(true);
        return loadAppData();
      })
      .catch(() => {
        setHealthCheckOk(false);
        setIsLoading(false);
      });
  }, [dispatch]);

  if (isLoading) return loading();
  if (!healthCheckOk) {
    return (
      <React.Suspense fallback={loading()}>
        <Error500 />
      </React.Suspense>
    );
  }

  return (
    <div style={{ backgroundColor: "#EEEEEE", minHeight: "100vh" }}>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <React.Suspense fallback={loading()}>
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route exact path="/login" name="Login Page" element={<Login />} />
            {letRegister && (
              <Route
                exact
                path="/register"
                name="Register Page"
                element={<Register />}
              />
            )}
            <Route
              exact
              path="/forgotPassword"
              name="Forget Password Page"
              element={<ForgotPassword />}
            />
            <Route
              exact
              path="/resetPassword/:token"
              name="Reset Password Page"
              element={<ResetPassword />}
            />
            <Route
              exact
              path="/confirm/:token"
              name="Confirm Email"
              element={<Confirmation />}
            />
            <Route
              exact
              path="/terms"
              name="Terms and Conditions"
              element={<LegalTerms mdFile="terms_conditions.md" />}
            />
            <Route path="*" element={<Error404 />} />
            <Route
              exact
              path="/"
              element={
                <PrivateRoute accessRoles={["Admin", "User", "Manager"]}>
                  <DefaultLayout />
                </PrivateRoute>
              }
            >
              {routes.map((route) => {
                return (
                  route.element && (
                    <Route
                      key={route.name}
                      path={route.path}
                      exact={route.exact}
                      name={route.name}
                      index={route.index || false}
                      element={
                        <PrivateRoute accessRoles={route.roles}>
                          <route.element />
                        </PrivateRoute>
                      }
                    />
                  )
                );
              })}
            </Route>
          </Routes>
          <ToastContainer />
        </React.Suspense>
      </BrowserRouter>
    </div>
  );
};

export default App;
